import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import { getDecodedToken, getLocalStorage } from "./localStorage";
import { AUTH_TOKEN, COUNTRY_CODE_TO_ID, MESSAGE_KEY, PRACTICE_TOKEN } from "./constants";
import { TATVA_PRACTICE_WEBVIEW } from "../envConstants";
import { isPracticeTokenValidForUser } from "./sessionGuard";
import html2canvas from "html2canvas";
import moment from "moment";
import { message } from "antd";

dayjs.extend(duration);
dayjs.extend(relativeTime);

interface DataItem {
  count: number;
  yearMonth: string;
}

export const getInitials = (nameStr: string): string | null => {
  // Remove any surrounding quotes and trim the name
  const name = nameStr.replace(/"/g, "").trim();

  // Split the name into parts (first and last)
  const nameParts = name.split(" ").filter(Boolean); // Filters out any empty strings

  // Return initials based on the condition
  if (nameParts.length > 1) {
    // Return first letters of both first and last name, in uppercase
    return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
  } else if (nameParts.length === 1) {
    // Return first two letters of the first name, in uppercase
    return nameParts[0].slice(0, 2).toUpperCase();
  }

  return null; // Return null if the input is not valid
};

export const calculateDuration = (startDate: string, endDate: string) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  const diff = end.diff(start);

  const timeDuration = dayjs.duration(diff);

  const years = Math.floor(timeDuration.asYears());
  const months = Math.floor(timeDuration.asMonths() % 12); // Remaining months after full years
  const days = timeDuration.days();

  return { years, months, days };
};

export const formatPhoneNumber = (phoneNumber: string) => {
  const countryCode = `+${phoneNumber.slice(0, 2)}`;
  const mainNumber = phoneNumber.slice(2);
  return `${countryCode} ${mainNumber}`;
};

export const getCountryIdFromMobile = (mobile: string | undefined): number | null => {
  const m = String(mobile || "").replace(/\D/g, "");
  if (m.startsWith("977") && (m.length === 11 || m.length === 12)) {
    console.log("[pwa] countryFromMobile +977 -> 2");
    return COUNTRY_CODE_TO_ID["977"];
  }
  if (m.startsWith("91") && m.length === 12) {
    console.log("[pwa] countryFromMobile +91 -> 1");
    return COUNTRY_CODE_TO_ID["91"];
  }
  if (m.startsWith("27") && (m.length === 11 || m.length === 12)) {
    console.log("[pwa] countryFromMobile +27 -> 3");
    return COUNTRY_CODE_TO_ID["27"];
  }
  if (m.startsWith("63") && (m.length === 12 || m.length === 13)) {
    console.log("[pwa] countryFromMobile +63 -> 4");
    return COUNTRY_CODE_TO_ID["63"];
  }
  console.log("[pwa] countryFromMobile: no match", m.length ? "len=" + m.length : "(no mobile)");
  return null;
};

export const captureShort = async (shortRef: any) => {
  try {
    // Capture the element as a canvas
    const canvas = await html2canvas(shortRef.current, { scale: 3 });
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.8)
    );

    if (!blob) throw new Error("Failed to capture image");

    const file = new File([blob], "short-snapshot.jpg", { type: "image/jpeg" });

    // Check for Web Share API
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: "Check out this healthcare short!",
        text: "Here's a quick healthcare tip for you!",
      });
    } else {
      // Fallback: Copy a shareable link to the clipboard
      await navigator.clipboard.writeText("https://example.com/shareable-link");
      alert("Sharing not supported; link copied to clipboard!");
    }
  } catch (error) {
    console.error("Sharing failed:", error);
    alert("An error occurred while sharing");
  }
};

export const fillMissingMonths = (data: DataItem[]): DataItem[] => {
  // Sort input data by yearMonth in ascending order
  const sortedData = data.sort((a, b) =>
    moment(a.yearMonth).diff(moment(b.yearMonth))
  );

  // Determine the range of months
  const startMonth = moment(sortedData[0]?.yearMonth); // Earliest month in data
  const currentMonth = moment().startOf("month"); // Current month

  const result: DataItem[] = [];
  let pointer = startMonth.clone(); // Start iterating from startMonth

  // Loop through all months from startMonth to currentMonth
  while (pointer.isSameOrBefore(currentMonth)) {
    const yearMonth = pointer.format("YYYY-MM");
    const existingData = sortedData.find(
      (item) => item.yearMonth === yearMonth
    );

    if (existingData) {
      // If the month exists in the input, add it
      result.push(existingData);
    } else {
      // If the month is missing, add it with count = 0
      result.push({ count: 0, yearMonth });
    }

    // Move to the next month
    pointer = pointer.add(1, "month");
  }

  // Sort the result in descending order of yearMonth
  return result.sort((a, b) => moment(b.yearMonth).diff(moment(a.yearMonth)));
};

export function calculateAge(dateOfBirth: string) {
  const dob = new Date(dateOfBirth);
  const currentDate = new Date();

  const ageInMilliseconds = currentDate.getTime() - dob.getTime();
  const ageInYears = Math.floor(ageInMilliseconds / (365.25 * 24 * 60 * 60 * 1000));
  const ageInMonths = Math.floor((ageInMilliseconds % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000));
  const ageInDays = Math.floor((ageInMilliseconds % (30.44 * 24 * 60 * 60 * 1000)) / (24 * 60 * 60 * 1000));

  return { years: ageInYears, months: ageInMonths, days: ageInDays };
};

export const sendMessageToParent = (eventName: string, data = {}) => {
  try {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ action: eventName, data })
      );
    } else {
      console.warn("ReactNativeWebView is not available on the window object.");
    }
  } catch (error) {
    console.log("sendMessageToParent ERROR", error);
  }
};

export const errorMessage = (error: any, status: any = 'error') => {
  return message.open({
    key: MESSAGE_KEY,
    type: status,
    content: typeof error === 'object' ? error.response.data.message : error,
    className: 'text-white',
    duration: 2,
  });
};

export const successMessage = (msg: any) => {
  message.success({
    key: MESSAGE_KEY,
    content: typeof msg === 'object' ? msg.message : msg,
    className: 'text-white',
    duration: 2,
  });
};

// Helper function to check if 48 hours have passed since modal was last closed
export const shouldShowProfileModal = () => {
  const lastClosedTime = localStorage.getItem("profileModalLastClosed");
  if (!lastClosedTime) return true;

  const now = Date.now();
  const lastClosed = parseInt(lastClosedTime);
  const fortyEightHours = 48 * 60 * 60 * 1000; // 48 hours in milliseconds

  return now - lastClosed >= fortyEightHours;
};

export const navigateToTatvaPractice = async (toOfferings = true) => {
  let tatvaPracticeToken = getLocalStorage(PRACTICE_TOKEN);
  const token = getLocalStorage(AUTH_TOKEN);

  // A cached token from another doctor's session (same device) or an expired one would
  // open TatvaPractice as the wrong user, so mint a fresh one instead of reusing it.
  if (!isPracticeTokenValidForUser(tatvaPracticeToken)) {
    tatvaPracticeToken = null;
  }

  // If practice token doesn't exist, fetch it from setToken API
  if (!tatvaPracticeToken) {
    try {
      const tokenData: any = getDecodedToken();
      
      if (!tokenData?.user) {
        console.error('Cannot fetch SSO token: Invalid token data');
        return;
      }
      
      let mobileNumber = tokenData?.user?.mobileNumber || '';
      if (mobileNumber && mobileNumber.startsWith('91')) {
        mobileNumber = mobileNumber.substring(2);
      }
      
      // API expects pm_id for doctor; use localStorage when set (from getPediaDoctorDetails), else JWT doctorUniqueId
      const pmId = typeof localStorage !== 'undefined' ? localStorage.getItem('pm_id') : null;
      const doctorUniqueId = (pmId && pmId.trim()) || tokenData.user?.doctorUniqueId || '';
      const { setSSOToken } = await import('../pages/home/service');
      const response = await setSSOToken({
        doctor_unique_id: doctorUniqueId,
        mobile_no: mobileNumber,
        // hospitalId is comma-separated for doctors with several clinics; the practice token
        // must name one clinic, or TatvaPractice can't match it and shows "Select Clinic".
        clinic_id: String(tokenData.user?.hospitalId ?? '').split(',')[0],
        hospital_business_id: tokenData.user?.businessId || '',
        user_id: tokenData.user?.tpUserId || '',
      });
      
      if (response?.data?.token) {
        tatvaPracticeToken = response.data.token;
        localStorage.setItem(PRACTICE_TOKEN, tatvaPracticeToken);
      }
    } catch (error) {
      console.error('Error fetching SSO token:', error);
    }
  }
  
  if (token && tatvaPracticeToken) {
    const encodedAuthToken = encodeURIComponent(tatvaPracticeToken);
    const encodedMedecoToken = encodeURIComponent(token);
    const url = toOfferings
      ? `${TATVA_PRACTICE_WEBVIEW}/our-offerings?from=home&authToken=${encodedAuthToken}&medecoToken=${encodedMedecoToken}`
      : `${TATVA_PRACTICE_WEBVIEW}/?authToken=${encodedAuthToken}&medecoToken=${encodedMedecoToken}`;
    window.location.href = url;
  }
}

export const navigateToTatvaPracticeWithAuthToken = (params: {
  doctorName?: string;
  speciality?: number;
  mobileNumber?: string;
  token: string;
}) => {

  if (!params.token) {
    console.error('Cannot navigate to TatvaPractice: Missing authentication token');
    errorMessage('Unable to access TatvaPractice. Please login again.');
    return;
  }

  try {
    // const url = `${TATVA_PRACTICE_WEBVIEW}/final-setup?medecoToken=${encodeURIComponent(params?.token)}&mobileNo=${encodeURIComponent(params?.mobileNumber ?? '')}&isFromMedeco=true&speciality=${encodeURIComponent(params?.doctorName ?? '')}&fullName=${encodeURIComponent(params?.doctorName ?? '')}`;
    const url = `${TATVA_PRACTICE_WEBVIEW}/final-setup?medecoToken=${encodeURIComponent(params?.token)}&mobileNo=${encodeURIComponent(params?.mobileNumber ?? '')}&isFromMedeco=true&speciality=${encodeURIComponent(params?.speciality ?? '')}&fullName=${encodeURIComponent(params?.doctorName ?? '')}`;
    window.location.href = url;
  } catch (error) {
    console.error('Error navigating to TatvaPractice:', error);
    errorMessage('Failed to navigate to TatvaPractice. Please try again.');
  }
}

export const trackEvent = (eventName: string, attributes: Record<string, any>) => {
  (window as any).Moengage?.track_event(eventName, attributes);
};
